// CreateDevice.js
import React, { useState, useCallback, useEffect } from "react";
import SubTitle from "../common/SubTitle";
import { Box, Typography, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import DeviceDetails from "./DeviceDetails";
import SimDetails from "./SimDetails";
import InstallationDetails from "./InstallationDetails";
import ExecutiveDetails from "./ExecutiveDetails";
import InstalledPhotos from "./InstalledPhotos";
import {
  createTrackingDevice,
  updateTrackingDevice,
  getTrackingDeviceById,
  formatDeviceData,
  validateDevice,
  isValidEmail,
  isValidIMEI,
  isValidPhoneNumber,
} from "./deviceApi";

export default function CreateDevice() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { deviceId } = useParams();
  const isEditMode = Boolean(deviceId) && !isNaN(Number(deviceId));

  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [existingSimCardId, setExistingSimCardId] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  const [formData, setFormData] = useState({
    deviceId: "",
    imeiNumber: "",
    model: "",
    vendorBrand: "",
    workingCondition: "",
    status: "",
    deviceImage: null,
    deviceImagePreview: null,
    installationDate: "",
    organizationName: "",
    city: "",
    site: "",
    vehicle: null,
    vehicleRegistrationNumber: "",
    vehicleModel: "",
    executiveFullName: "",
    executivePhoneNumber: "",
    executiveEmail: "",
    remarks: "",
    simCard: {
      simImei: "",
      mobileNumber: "",
      nextRechargeDate: "",
      provider: "",
    },
  });

  // Separate state for installation photos - initialize with proper structure
  const [installedPhotos, setInstalledPhotos] = useState([
    {
      id: "photo-1",
      photoNumber: 1,
      file: null,
      fileName: "",
      preview: null,
      isExisting: false,
      isUploaded: false,
      needsReplacement: false,
      serverId: null,
    },
  ]);
  const [errors, setErrors] = useState({});

  // Load device data if in edit mode
  useEffect(() => {
    if (isEditMode && deviceId) {
      loadDeviceData();
    }
  }, [isEditMode, deviceId]);

  const loadDeviceData = async () => {
    try {
      setIsLoading(true);
      const response = await getTrackingDeviceById(deviceId);
      const deviceData = response.data; // Access the data property from response

      // Store existing simCard ID
      if (deviceData.SimCard?.id) {
        // Changed from simCard to SimCard
        setExistingSimCardId(deviceData.SimCard.id);
      }

      // Set selected organization directly from vehicle's organization data
      if (deviceData.Vehicle?.organization) {
        // Changed from vehicle to Vehicle
        setSelectedOrganization({
          id: deviceData.Vehicle.organization.id,
          name: deviceData.Vehicle.organization.name,
        });
      }

      setFormData({
        id: deviceData.id,
        deviceId: deviceData.deviceId,
        imeiNumber: deviceData.imeiNumber || "",
        model: deviceData.model || "",
        vendorBrand: deviceData.vendorBrand || "",
        workingCondition: deviceData.workingCondition || "",
        status: deviceData.status || "",
        deviceImage: null,
        deviceImagePreview: deviceData.deviceImageUrl || null,
        installationDate: deviceData.installationDate?.split("T")[0] || "",
        // Organization details from vehicle
        organizationName: deviceData.Vehicle?.organization?.name || "",
        city: deviceData.Vehicle?.organization?.city || "",
        site: deviceData.Vehicle?.siteId || "",
        // Vehicle details
        vehicle: deviceData.Vehicle
          ? {
              id: deviceData.Vehicle.id,
              name: deviceData.Vehicle.name,
              registrationNumber: deviceData.Vehicle.registrationNumber,
              model: deviceData.Vehicle.model,
            }
          : null,
        vehicleRegistrationNumber: deviceData.Vehicle?.registrationNumber || "",
        vehicleModel: deviceData.Vehicle?.model || "",
        // Executive details
        executiveFullName: deviceData.executiveFullName || "",
        executivePhoneNumber: deviceData.executivePhoneNumber || "",
        executiveEmail: deviceData.executiveEmail || "",
        remarks: deviceData.remarks || "",
        // SIM card details
        simCard: {
          simImei: deviceData.SimCard?.imeiNumber || "", // Changed from simCard to SimCard
          mobileNumber: deviceData.SimCard?.simNumber || "",
          nextRechargeDate: deviceData.SimCard?.planExpiry?.split("T")[0] || "",
          provider: deviceData.SimCard?.provider || "",
        },
      });

      // Handle photos if they exist
      if (deviceData.Document && deviceData.Document.length > 0) {
        // Changed from photos to Document
        const photoData = deviceData.Document.map((photo, index) => ({
          id: `photo-${index + 1}`,
          file: null,
          fileName: photo.location ? photo.location.split("/").pop() : "",
          preview: photo.url,
          isExisting: true,
          isUploaded: true,
          serverId: photo.id,
          needsReplacement: false,
          createdAt: photo.createdAt,
          updatedAt: photo.updatedAt,
        }));
        setInstalledPhotos(photoData);
      }
    } catch (error) {
      console.error("Error loading device data:", error);
      setSubmitError(t("Failed to load device data. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      if (
        formData.deviceImagePreview &&
        formData.deviceImagePreview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(formData.deviceImagePreview);
      }
      // Cleanup photo previews
      installedPhotos.forEach((photo) => {
        if (photo.preview && photo.preview.startsWith("blob:")) {
          URL.revokeObjectURL(photo.preview);
        }
      });
    };
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

    // ONLY validate required fields for basic form validation
    if (!formData.deviceId?.trim()) {
      newErrors.deviceId = t("Device ID is required");
    }

    if (!formData.status?.trim()) {
      newErrors.status = t("Status is required");
    }

    // Device IMEI validation
    if (
      formData.imeiNumber?.trim() &&
      !isValidIMEI(formData.imeiNumber.trim())
    ) {
      newErrors.imeiNumber = t("IMEI number must be numeric");
    }

    // SIM IMEI validation
    if (
      formData.simCard?.simImei?.trim() &&
      !isValidIMEI(formData.simCard.simImei.trim())
    ) {
      newErrors.simImei = t("SIM IMEI must be numeric");
    }

    // Phone number validation
    if (
      formData.executivePhoneNumber?.trim() &&
      !isValidPhoneNumber(formData.executivePhoneNumber.trim())
    ) {
      newErrors.executivePhoneNumber = t("Please enter a valid phone number");
    }

    // Mobile number validation (SIM card)
    if (
      formData.simCard?.mobileNumber?.trim() &&
      !isValidPhoneNumber(formData.simCard.mobileNumber.trim())
    ) {
      newErrors.mobileNumber = t("Please enter a valid mobile number");
    }

    // Email validation
    if (
      formData.executiveEmail?.trim() &&
      !isValidEmail(formData.executiveEmail.trim())
    ) {
      newErrors.executiveEmail = t("Please enter a valid email address");
    }

    // Date validations
    // Installation date should be past or present (not future)
    if (formData.installationDate) {
      const installationDate = new Date(formData.installationDate);
      installationDate.setHours(0, 0, 0, 0);

      if (installationDate > today) {
        newErrors.installationDate = t(
          "Installation date cannot be in the future"
        );
      }
    }

    // Next recharge date should be future (not past)
    if (formData.simCard?.nextRechargeDate) {
      const rechargeDate = new Date(formData.simCard.nextRechargeDate);
      rechargeDate.setHours(0, 0, 0, 0);

      if (rechargeDate < today) {
        newErrors.nextRechargeDate = t(
          "Next recharge date cannot be in the past"
        );
      }
    }

    // Only validate optional fields if they have values (and only basic format checks)
    if (formData.deviceImage && formData.deviceImage instanceof File) {
      if (formData.deviceImage.size > 3 * 1024 * 1024) {
        newErrors.deviceImage = t("Image size should be less than 3MB");
      }
      if (!formData.deviceImage.type.startsWith("image/")) {
        newErrors.deviceImage = t("Please select a valid image file");
      }
    }

    // Update errors state immediately so they can be displayed
    setErrors(newErrors);

    // Return validation result
    const isValid = Object.keys(newErrors).length === 0;

    // If there are errors, ensure they are properly captured for the submit handler
    if (!isValid) {
      console.log("Validation errors:", newErrors);
    }

    return isValid;
  }, [
    formData.deviceId,
    formData.status,
    formData.imeiNumber,
    formData.simCard?.simImei,
    formData.executivePhoneNumber,
    formData.simCard?.mobileNumber,
    formData.executiveEmail,
    formData.installationDate,
    formData.simCard?.nextRechargeDate,
    formData.deviceImage,
    t,
  ]);
  const handleInputChange = useCallback(
    (e, field) => {
      const value = e.target.value;

      // Handle nested simCard fields
      if (field.startsWith("simCard.")) {
        const simCardField = field.split(".")[1];
        setFormData((prev) => ({
          ...prev,
          simCard: {
            ...prev.simCard,
            [simCardField]: value,
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [field]: value,
        }));
      }

      // Clear errors for the field
      const errorField = field.startsWith("simCard.")
        ? field.split(".")[1]
        : field;
      if (errors[errorField]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[errorField];
          return newErrors;
        });
      }
    },
    [errors]
  );

  const handleImageUpload = useCallback(
    (e) => {
      const file = e.target.files[0];
      if (file) {
        // Validate file
        if (file.size > 10 * 1024 * 1024) {
          alert(t("Image size should be less than 10MB"));
          return;
        }

        if (!file.type.startsWith("image/")) {
          alert(t("Please select a valid image file"));
          return;
        }

        // Clean up previous preview if it was a blob URL
        if (
          formData.deviceImagePreview &&
          formData.deviceImagePreview.startsWith("blob:")
        ) {
          URL.revokeObjectURL(formData.deviceImagePreview);
        }

        const preview = URL.createObjectURL(file);

        setFormData((prev) => ({
          ...prev,
          deviceImage: file,
          deviceImagePreview: preview,
        }));

        if (errors.deviceImage) {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.deviceImage;
            return newErrors;
          });
        }
      }
    },
    [formData.deviceImagePreview, errors, t]
  );

  const handleRemoveImage = useCallback(async () => {
    // If it's an existing image (from server)
    if (
      formData.deviceImagePreview &&
      !formData.deviceImagePreview.startsWith("blob:")
    ) {
      try {
        await axios.delete(`${config.baseURL}/upload/image`, {
          params: {
            name: "DeviceImage",
            associateEntityId: deviceId,
            associatedWith: "Device",
            type: "profile",
          },
          withCredentials: true,
        });
      } catch (error) {
        console.error("Error deleting device image:", error);
        setSubmitError(t("Failed to delete device image"));
        return;
      }
    }

    // Clean up blob URL if exists
    if (
      formData.deviceImagePreview &&
      formData.deviceImagePreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(formData.deviceImagePreview);
    }

    setFormData((prev) => ({
      ...prev,
      deviceImage: null,
      deviceImagePreview: null,
    }));

    // Clear any image-related errors
    if (errors.deviceImage) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.deviceImage;
        return newErrors;
      });
    }
  }, [formData.deviceImagePreview, deviceId, errors, t]);

  const handleSubmit = async () => {
    setSubmitError("");
    setSubmitSuccess("");

    // Run form validation first
    if (!validateForm()) {
      // Get all validation errors and display them
      const errorMessages = Object.values(errors).filter((error) => error);
      if (errorMessages.length > 0) {
        setSubmitError(errorMessages.join(". "));
      } else {
        setSubmitError(t("Please fix the errors"));
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Use the updated formatDeviceData function with simCard ID for updates
      const deviceData = formatDeviceData(
        formData,
        isEditMode,
        existingSimCardId
      );
      const validation = validateDevice(deviceData);

      if (!validation.isValid) {
        // Show specific validation errors from deviceApi
        setSubmitError(validation.errors.join(". "));
        setIsSubmitting(false);
        return;
      }

      let response, currentDeviceId;

      if (isEditMode) {
        // Update existing device
        deviceData.id = deviceId;
        response = await updateTrackingDevice(deviceData);
        currentDeviceId = deviceId;
        console.log("Device updated with ID:", currentDeviceId);
      } else {
        // Create new device
        response = await createTrackingDevice(deviceData);
        currentDeviceId =
          response.data?.device?.id || response.data?.id || response.id;
        console.log("Device created with ID:", currentDeviceId);
      }

      // Upload device image if provided
      if (
        formData.deviceImage &&
        formData.deviceImage instanceof File &&
        currentDeviceId
      ) {
        try {
          // Wait a moment to ensure device is available in backend
          await new Promise((resolve) => setTimeout(resolve, 500));

          const imageFormData = new FormData();
          imageFormData.append("image", formData.deviceImage);

          const imageResponse = await axios.post(
            `${config.baseURL}/upload/image?name=${encodeURIComponent(
              "DeviceImage"
            )}&associateEntityId=${currentDeviceId}&associatedWith=Device&type=profile`,
            imageFormData,
            {
              withCredentials: true,
              headers: { "Content-Type": "multipart/form-data" },
            }
          );

          console.log(
            "Device image uploaded successfully:",
            imageResponse.data
          );
        } catch (imageError) {
          console.error("Device image upload failed:", imageError);
          const imageErrorMsg =
            imageError.response?.data?.message ||
            imageError.response?.data?.error ||
            t("Image upload failed");
          setSubmitError(
            t(
              `Device ${
                isEditMode ? "updated" : "created"
              }, but image upload failed: ${imageErrorMsg}`
            )
          );
          setIsSubmitting(false);
          return;
        }
      }

      // Return the device ID for the photo manager to use
      return currentDeviceId;
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} device:`,
        error
      );

      // Extract specific error message from different possible response structures
      let errorMessage = t(
        `An error occurred while ${
          isEditMode ? "updating" : "creating"
        } the device. Please try again.`
      );

      if (error.response?.data) {
        // Try different possible error message locations
        if (typeof error.response.data === "string") {
          // Handle HTML error responses (e.g. Express default error pages)
          const htmlString = error.response.data;
          if (htmlString.includes("<pre>") && htmlString.includes("</pre>")) {
            const preMatch = htmlString.match(/<pre>([\s\S]*?)<\/pre>/);
            if (preMatch) {
              // Strip "Error: " prefix and stack trace, keep only the message
              const rawText = preMatch[1]
                .replace(/<br\s*\/?>/gi, " ")
                .replace(/&nbsp;/g, " ")
                .replace(/&amp;/g, "&")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .trim();
              // Take only the first line (the actual error message)
              errorMessage = rawText
                .split(/\n|at\s/)[0]
                .replace(/^Error:\s*/i, "")
                .trim();
            }
          } else {
            errorMessage = htmlString;
          }
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.errors) {
          // Handle array of errors
          if (Array.isArray(error.response.data.errors)) {
            errorMessage = error.response.data.errors.join(". ");
          } else if (typeof error.response.data.errors === "object") {
            errorMessage = Object.values(error.response.data.errors).join(". ");
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      // If the error is about a duplicate device ID, show it as a field error
      if (
        errorMessage.toLowerCase().includes("device id already exists") ||
        errorMessage.toLowerCase().includes("device with this device id")
      ) {
        setErrors((prev) => ({
          ...prev,
          deviceId: errorMessage,
        }));
        setSubmitError("");
      } else {
        setSubmitError(errorMessage);
      }
      setIsSubmitting(false);
      throw error;
    }
  };

  const handleCancel = () => {
    // Clean up any blob URLs
    if (
      formData.deviceImagePreview &&
      formData.deviceImagePreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(formData.deviceImagePreview);
    }
    installedPhotos.forEach((photo) => {
      if (photo.preview && photo.preview.startsWith("blob:")) {
        URL.revokeObjectURL(photo.preview);
      }
    });
    navigate(-1);
  };

  const handleComplete = () => {
    setSubmitSuccess(
      t(`Device ${isEditMode ? "updated" : "created"} successfully!`)
    );
    console.log(
      `Device and photos ${isEditMode ? "updated" : "saved"} successfully`
    );

    // Navigate back after successful operation
    setTimeout(() => {
      navigate(-1);
    }, 2000);
  };

  // Show loading state while fetching device data (edit mode only)
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <Typography variant="h6">{t("Loading device data...")}</Typography>
      </Box>
    );
  }

  // Updated form validation logic - only deviceId and status are required
  const isFormValid =
    formData.deviceId?.trim() &&
    formData.status?.trim() &&
    Object.keys(errors).length === 0;

  const RequiredLabel = ({ label }) => (
    <Box sx={{ mb: 1, display: "flex", alignItems: "center" }}>
      <Typography variant="body2" component="span">
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="span"
        color="error"
        sx={{ ml: 0.5 }}
      >
        *
      </Typography>
    </Box>
  );

  const StandardLabel = ({ label }) => (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2">{label}</Typography>
    </Box>
  );

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;
  const orgId = JSON.parse(localStorage.getItem("userState"))?.organization?.id;

  return (
    <>
      <SubTitle
        title={isEditMode ? "Update Device" : "Create Device"}
        path={`${orgName} > Devices > ${isEditMode ? "Update" : "Create "}`}
      />

      {submitSuccess && (
        <Alert severity="success" sx={{ mx: { xs: 2.5, md: 5 }, mb: 2 }}>
          {submitSuccess}
        </Alert>
      )}
      {submitError && (
        <Alert severity="error" sx={{ mx: { xs: 2.5, md: 5 }, mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <DeviceDetails
          formData={formData}
          handleInputChange={handleInputChange}
          handleImageUpload={handleImageUpload}
          handleRemoveImage={handleRemoveImage}
          RequiredLabel={RequiredLabel}
          StandardLabel={StandardLabel}
          errors={errors}
          t={t}
        />
        <SimDetails
          formData={formData}
          handleInputChange={handleInputChange}
          RequiredLabel={RequiredLabel}
          StandardLabel={StandardLabel}
          errors={errors}
          t={t}
        />
        <InstallationDetails
          formData={formData}
          handleInputChange={handleInputChange}
          RequiredLabel={RequiredLabel}
          StandardLabel={StandardLabel}
          errors={errors}
          t={t}
          selectedOrganization={selectedOrganization}
          setSelectedOrganization={setSelectedOrganization}
        />
        <ExecutiveDetails
          formData={formData}
          handleInputChange={handleInputChange}
          RequiredLabel={RequiredLabel}
          StandardLabel={StandardLabel}
          errors={errors}
          t={t}
        />
        <InstalledPhotos
          deviceId={isEditMode ? deviceId : null}
          photos={installedPhotos}
          setPhotos={setInstalledPhotos}
          isEditing={isEditMode}
          onComplete={handleComplete}
          orgId={orgId}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isDetailsFormValid={isFormValid}
          triggerFormValidation={validateForm}
        />
      </Box>
      <Box sx={{ height: "40px" }}></Box>
    </>
  );
}
